/**
 * Family bundle product page.
 *
 * family-bundle-product: the section wrapper. Keeps the sticky info column in
 * view the same way product-info.js does on the normal product page, which
 * this template does not load.
 *
 * family-bundle-builder: member, product, size and quantity selection, the
 * on-page bundle, and the all-or-nothing add to cart.
 */

if (!customElements.get('family-bundle-product')) {
  class FamilyBundleProduct extends HTMLElement {
    connectedCallback() {
      this.infoContainer = this.querySelector('.product__info-wrapper .product__column-sticky');
      this.mediaWrapper = this.querySelector('.product__media-wrapper');
      if (!this.infoContainer || !this.mediaWrapper) return;

      this.lastScrollY = window.scrollY;
      this.currentOffset = 0;
      this.isInitialized = false;
      this.rafId = null;

      this.onScroll = () => {
        if (this.rafId) return;

        this.rafId = requestAnimationFrame(() => {
          this.updateStickyOffset();
          this.rafId = null;
        });
      };

      this.resizeObserver = new ResizeObserver(() => this.updateStickyOffset());
      this.resizeObserver.observe(this.infoContainer);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.updateStickyOffset();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    /**
     * Same rule as product-info.js updateStickyInfo(): a short column sticks at
     * 15rem from the top; a tall one scrolls with the page 1:1 so its bottom
     * can be reached, clamped between its top and bottom.
     */
    updateStickyOffset() {
      const remInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const minTopOffset = 15 * remInPixels;
      const effectiveViewportHeight = window.innerHeight - minTopOffset;
      const infoHeight = this.infoContainer.offsetHeight;

      if (infoHeight <= effectiveViewportHeight) {
        this.infoContainer.style.setProperty('--product-info-top', `${minTopOffset}px`);
        return;
      }

      const maxOffset = infoHeight - effectiveViewportHeight;
      const mediaRect = this.mediaWrapper.getBoundingClientRect();
      const mediaInView = mediaRect.bottom > 0 && mediaRect.top < window.innerHeight;
      if (!mediaInView) return;

      if (!this.isInitialized) {
        this.currentOffset = Math.max(0, Math.min(-mediaRect.top, maxOffset));
        this.isInitialized = true;
      }

      const scrollDelta = window.scrollY - this.lastScrollY;
      this.lastScrollY = window.scrollY;

      if (scrollDelta !== 0) {
        this.currentOffset = Math.max(0, Math.min(this.currentOffset + scrollDelta, maxOffset));
      }

      this.infoContainer.style.setProperty('--product-info-top', `${minTopOffset - this.currentOffset}px`);
    }
  }

  customElements.define('family-bundle-product', FamilyBundleProduct);
}

if (!customElements.get('family-bundle-builder')) {
  /**
   * @typedef {Object} BundleVariant
   * @property {number} id
   * @property {string} size
   * @property {boolean} available
   * @property {number|null} max - Stock or quantity-rule limit; null when unlimited
   * @property {number} price - In cents
   */

  /**
   * @typedef {Object} BundleProduct
   * @property {string} title
   * @property {string} handle
   * @property {string} image
   * @property {BundleVariant[]} variants
   */

  /**
   * @typedef {Object} BundleLine
   * @property {string} member
   * @property {string} productId
   * @property {number} variantId
   * @property {number} quantity
   */

  class FamilyBundleBuilder extends HTMLElement {
    /** @type {BundleLine[]} */
    lines = [];
    /** @type {Set<number>} */
    soldOutVariantIds = new Set();
    member = null;
    productId = null;
    variantId = null;
    editingIndex = null;
    isAddingToCart = false;

    connectedCallback() {
      const productsData = this.querySelector('[data-family-bundle-products]');
      const stringsData = this.querySelector('[data-family-bundle-strings]');
      if (!productsData || !stringsData) return;

      /** @type {Record<string, BundleProduct>} */
      this.products = JSON.parse(productsData.textContent);
      this.strings = JSON.parse(stringsData.textContent);

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.styleFindMyFit();
    }

    /**
     * Opens the Quick view for a style: fetches sections/family-bundle-quick-view rendered
     * for that product, puts it in the modal and shows it. The modal is the theme's
     * modal-dialog, which moves itself to <body>, so it is found by id. The button is
     * aria-disabled while loading, so a second click before the product arrives does nothing.
     * @param {HTMLButtonElement} button
     */
    async openQuickView(button) {
      const modal = document.getElementById(this.dataset.quickViewId);
      const content = modal?.querySelector('[data-quick-view-content]');
      if (typeof modal?.show !== 'function' || !content || button.getAttribute('aria-disabled') === 'true') return;

      this.quickViewController?.abort();
      this.quickViewController = new AbortController();
      this.setQuickViewLoading(button, true);

      try {
        const url = new URL(button.dataset.productUrl, window.location.origin);
        url.searchParams.set('section_id', 'family-bundle-quick-view');

        const response = await fetch(url, { signal: this.quickViewController.signal });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const product = html.querySelector('.family-bundle-quick-view__product');
        if (!product) return;

        this.quickViewSwiper?.destroy(true, true);
        content.replaceChildren(product);
        this.fillQuickViewShipping(product);
        modal.show(button);
        this.startQuickViewSlider(product);
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        this.setQuickViewLoading(button, false);
      }
    }

    /**
     * Spinner in place of the See details label while the product loads.
     * @param {HTMLButtonElement} button
     * @param {boolean} isLoading
     */
    setQuickViewLoading(button, isLoading) {
      button.classList.toggle('loading', isLoading);
      button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);

      if (isLoading) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    }

    /**
     * The section is rendered for the style alone, so a style without its own Shipping &
     * Returns entry gets the bundle page's fallback, rendered once in this snippet. With
     * neither, the accordion stays hidden.
     * @param {HTMLElement} product
     */
    fillQuickViewShipping(product) {
      const shipping = product.querySelector('[data-quick-view-shipping]');
      const fallback = this.querySelector('[data-quick-view-shipping-fallback]');
      if (!shipping || !fallback) return;

      shipping.querySelector('[data-quick-view-shipping-content]').replaceChildren(fallback.content.cloneNode(true));
      shipping.hidden = false;
    }

    /**
     * The Quick view photos, with dots. Images are not draggable so a mouse drag swipes.
     * Swiper may still be loading; a product replaced meanwhile is skipped.
     * @param {HTMLElement} product
     */
    async startQuickViewSlider(product) {
      const slider = product.querySelector('[data-quick-view-slider]');
      if (!slider) return;

      const Swiper = await window.pfLoadSwiper().catch(() => null);
      if (!Swiper || !product.isConnected) return;

      for (const image of slider.querySelectorAll('img')) {
        image.draggable = false;
      }

      const dots = product.querySelector('[data-quick-view-dots]');
      this.quickViewSwiper = new Swiper(slider, {
        slidesPerView: 1,
        pagination: dots ? { el: dots, clickable: true } : false,
      });
    }

    /**
     * Find My Fit is the vendor's <quidget-button>. Its text sits in an open
     * shadow root whose own stylesheet sets font, case and colour directly, so
     * page CSS cannot reach it. An adopted sheet applies after the vendor's and
     * only to this button. If the vendor renames #quidget_btn, it falls back to
     * their default look; nothing breaks.
     */
    async styleFindMyFit() {
      const button = this.querySelector('quidget-button');
      if (!button || typeof CSSStyleSheet === 'undefined') return;

      await customElements.whenDefined('quidget-button');
      if (!button.shadowRoot) return;

      const sheet = new CSSStyleSheet();
      sheet.replaceSync(`
        #quidget_btn p {
          color: #000;
          font-family: Poppins, sans-serif;
          font-size: 13px;
          font-weight: 500;
          line-height: 20px;
          text-decoration: underline;
          text-transform: uppercase;
        }
      `);
      button.shadowRoot.adoptedStyleSheets = [...button.shadowRoot.adoptedStyleSheets, sheet];
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.quickViewController?.abort();
      this.quickViewSwiper?.destroy(true, true);
    }

    get sizesContainer() {
      return this.querySelector('[data-sizes]');
    }

    get sizeList() {
      return this.querySelector('[data-size-list]');
    }

    get quantityContainer() {
      return this.querySelector('[data-quantity]');
    }

    get quantityInput() {
      return this.querySelector('[data-quantity-input]');
    }

    get addButton() {
      return this.querySelector('[data-add-to-bundle]');
    }

    get cartButton() {
      return this.querySelector('[data-add-to-cart]');
    }

    /** @param {MouseEvent} event */
    onClick(event) {
      const quickView = event.target.closest('[data-quick-view]');
      if (quickView) {
        // The end of a slider drag is not a See details click
        if (quickView.closest('[data-products-slider]')?.swiper?.allowClick === false) return;

        this.openQuickView(quickView);
        return;
      }

      const target = event.target.closest(
        '[data-member], [data-product-id], [data-quantity-step], [data-add-to-bundle], [data-add-to-cart], [data-line-action]'
      );
      if (!target) return;

      if (target.dataset.member) {
        this.selectMember(target.dataset.member);
      } else if (target.dataset.productId) {
        // A dot click, or the end of a photo or slider drag, is not a product pick
        const wasDragged =
          target.querySelector('[data-card-slider]')?.swiper?.allowClick === false ||
          target.closest('[data-products-slider]')?.swiper?.allowClick === false;
        if (event.target.closest('.swiper-pagination-bullet') || wasDragged) return;

        this.selectProduct(target.dataset.productId);
      } else if (target.dataset.quantityStep) {
        this.setQuantity(this.quantity + Number(target.dataset.quantityStep));
      } else if (target.hasAttribute('data-add-to-bundle')) {
        this.addToBundle();
      } else if (target.hasAttribute('data-add-to-cart')) {
        this.addToCart();
      } else if (target.dataset.lineAction === 'edit') {
        this.editLine(Number(target.dataset.index));
      } else if (target.dataset.lineAction === 'remove') {
        this.removeLine(Number(target.dataset.index));
      }
    }

    /** @param {Event} event */
    onChange(event) {
      if (event.target.matches('[data-size-input]')) {
        this.selectSize(Number(event.target.value));
      } else if (event.target.matches('[data-quantity-input]')) {
        this.setQuantity(Number(event.target.value));
      }
    }

    get quantity() {
      return Math.max(1, parseInt(this.quantityInput.value, 10) || 1);
    }

    /**
     * How many more of the selected size can go in the bundle: the variant's
     * max (see the snippet) minus what the bundle already holds of it. The
     * line being edited does not count against itself.
     * @returns {number}
     */
    get maxQuantity() {
      const max = this.currentVariant?.max;
      if (max == null) return Infinity;

      const inBundle = this.lines.reduce(
        (total, line, index) =>
          line.variantId === this.variantId && index !== this.editingIndex ? total + line.quantity : total,
        0
      );
      return Math.max(0, max - inBundle);
    }

    /**
     * Keeps the quantity between 1 and the stock limit, and disables − and +
     * at the ends, like the cart drawer's quantity-input.
     * @param {number} value
     */
    setQuantity(value) {
      const max = Math.max(1, this.maxQuantity);
      const quantity = Math.min(max, Math.max(1, Math.floor(value) || 1));

      this.quantityInput.value = quantity;
      this.quantityInput.max = Number.isFinite(max) ? max : '';
      this.querySelector('[data-quantity-step="-1"]').disabled = quantity <= 1;
      this.querySelector('[data-quantity-step="1"]').disabled = quantity >= max;
    }

    /**
     * Shows one member's products. Switching member drops the product and size
     * that are not yet in the bundle; bundle lines stay.
     * @param {string} member
     */
    selectMember(member) {
      this.member = member;

      for (const button of this.querySelectorAll('[data-member]')) {
        button.setAttribute('aria-pressed', String(button.dataset.member === member));
      }

      for (const panel of this.querySelectorAll('[data-member-panel]')) {
        panel.hidden = panel.dataset.memberPanel !== member;
      }

      this.initSlider(this.activePanel);
      this.clearProduct();
    }

    /**
     * Starts the panel's Swiper the first time it is shown: a hidden panel has
     * no width to measure. Cards are buttons, so only the quantity stepper is
     * excluded from dragging (Swiper's default list includes `button`). If Swiper
     * is still loading, this runs again once it arrives and the panel is still shown.
     * @param {HTMLElement} panel
     */
    initSlider(panel) {
      const slider = panel?.querySelector('[data-products-slider]');
      if (!slider || slider.swiper) return;

      if (typeof Swiper === 'undefined') {
        window.pfLoadSwiper().then(() => this.isConnected && !panel.hidden && this.initSlider(panel), () => {});
        return;
      }

      // Arrows are only rendered when the member has more than 3 products (see the snippet)
      const prevButton = panel.querySelector('[data-slider-prev]');
      const nextButton = panel.querySelector('[data-slider-next]');

      new Swiper(slider, {
        slidesPerView: 2.1,
        spaceBetween: 4,
        watchOverflow: true,
        noSwipingSelector: '.family-bundle-builder__stepper',
        navigation: prevButton && nextButton ? { prevEl: prevButton, nextEl: nextButton } : false,
        // Desktop: 'auto' uses the 208px card width from the CSS (Figma), not a share of the column
        breakpoints: {
          990: { slidesPerView: 'auto', spaceBetween: 16 },
        },
      });

      // Each card's photo carousel, as on the PLP card. `nested` keeps a photo
      // drag from also moving the silhouette slider. a11y is off because the
      // card is a <button>, and Swiper would put focusable dots inside it.
      for (const cardSlider of slider.querySelectorAll('[data-card-slider]')) {
        const slideCount = cardSlider.querySelectorAll('.swiper-slide').length;
        if (slideCount < 2) continue;

        for (const image of cardSlider.querySelectorAll('img')) {
          image.draggable = false;
        }

        new Swiper(cardSlider, {
          nested: true,
          loop: true,
          a11y: { enabled: false },
          pagination: { el: cardSlider.querySelector('.family-bundle-builder__card-dots'), clickable: true },
        });
      }
    }

    /** @param {string} productId */
    selectProduct(productId) {
      const product = this.products[productId];
      if (!product) return;

      this.productId = productId;
      this.variantId = null;

      const buttons = [...(this.activePanel?.querySelectorAll('[data-product-id]') || [])];

      for (const button of buttons) {
        button.setAttribute('aria-pressed', String(button.dataset.productId === productId));
      }

      this.renderSizes(product);
      this.sizesContainer.hidden = false;
      this.quantityContainer.hidden = true;
      this.setQuantity(1);
      this.updateAddButton();
    }

    get activePanel() {
      return this.querySelector(`[data-member-panel="${this.member}"]`);
    }

    clearProduct() {
      this.productId = null;
      this.variantId = null;

      for (const button of this.querySelectorAll('[data-product-id]')) {
        button.setAttribute('aria-pressed', 'false');
      }

      this.sizeList.replaceChildren();
      this.sizesContainer.hidden = true;
      this.quantityContainer.hidden = true;
      this.setQuantity(1);
      this.updateSizeHeading();
      this.updateAddButton();
    }

    /**
     * Every size is listed. Sold-out sizes stay selectable so the shopper sees
     * why they cannot be added, but they carry `.disabled` for the faded look.
     * @param {BundleProduct} product
     */
    renderSizes(product) {
      const groupName = `${this.id}-size`;
      const fragment = document.createDocumentFragment();

      for (const [index, variant] of product.variants.entries()) {
        const inputId = `${this.id}-size-${index}`;
        const input = document.createElement('input');
        input.type = 'radio';
        input.className = 'family-bundle-builder__size-input';
        input.id = inputId;
        input.name = groupName;
        input.value = String(variant.id);
        input.dataset.sizeInput = '';
        if (!variant.available) input.classList.add('disabled');

        const label = document.createElement('label');
        label.className = 'family-bundle-builder__size';
        label.htmlFor = inputId;
        label.textContent = variant.size;

        fragment.append(input, label);
      }

      this.sizeList.replaceChildren(fragment);
      this.updateSizeHeading();
    }

    /** @param {number} variantId */
    selectSize(variantId) {
      this.variantId = variantId;
      const variant = this.currentVariant;

      // The quantity control sits inside the selected card, under its name and price.
      this.activePanel
        ?.querySelector(`[data-product-id="${this.productId}"]`)
        ?.closest('.family-bundle-builder__product-item')
        ?.append(this.quantityContainer);
      this.quantityContainer.hidden = !variant?.available;
      // A new size starts at 1; editLine() sets the saved quantity after this call.
      this.setQuantity(1);
      this.updateSizeHeading();
      this.updateAddButton();
    }

    get currentProduct() {
      return this.productId ? this.products[this.productId] : null;
    }

    get currentVariant() {
      return this.currentProduct?.variants.find((variant) => variant.id === this.variantId) || null;
    }

    updateSizeHeading() {
      const variant = this.currentVariant;
      const selectedSize = this.querySelector('[data-selected-size]');
      const status = this.querySelector('[data-size-status]');

      selectedSize.textContent = variant ? variant.size : '';
      status.textContent = variant && !variant.available ? this.strings.outOfStock : '';
    }

    updateAddButton() {
      const canAdd = Boolean(this.currentVariant?.available) && this.maxQuantity >= 1;
      this.addButton.disabled = !canAdd;
      this.addButton.querySelector('[data-add-label]').textContent =
        this.editingIndex === null ? this.strings.addToBundle : this.strings.updateBundle;
    }

    /**
     * Adds one line per click, or replaces the line being edited. The same
     * product and size can appear on several lines; they are never merged.
     */
    addToBundle() {
      const variant = this.currentVariant;
      if (!variant?.available) return;

      const line = {
        member: this.member,
        productId: this.productId,
        variantId: variant.id,
        quantity: this.quantity,
      };
      const isUpdate = this.editingIndex !== null;

      if (isUpdate) {
        this.lines[this.editingIndex] = line;
      } else {
        this.lines.push(line);
      }

      this.editingIndex = null;
      this.soldOutVariantIds.delete(variant.id);
      this.announce(this.format(isUpdate ? this.strings.updated : this.strings.added, { item: this.lineName(line) }));
      this.closeMember();
      this.renderBundle();
    }

    /**
     * After an add, the member's product cards, sizes and quantity close and no
     * member is selected; the shopper picks a member again for the next item.
     * Edit reopens them through selectMember(). Focus moves to the first member
     * button because Add to Bundle, which had focus, is now disabled.
     */
    closeMember() {
      this.member = null;

      for (const button of this.querySelectorAll('[data-member]')) {
        button.setAttribute('aria-pressed', 'false');
      }

      for (const panel of this.querySelectorAll('[data-member-panel]')) {
        panel.hidden = true;
      }

      this.clearProduct();
      this.querySelector('[data-member]')?.focus({ preventScroll: true });
    }

    /** @param {number} index */
    editLine(index) {
      const line = this.lines[index];
      if (!line) return;

      this.editingIndex = index;
      this.selectMember(line.member);
      this.selectProduct(line.productId);

      // The edited style can be off screen in the reopened panel. A card click never
      // slides: the clicked card is already visible, and only the arrows move the slider.
      const cards = [...this.activePanel.querySelectorAll('[data-product-id]')];
      this.activePanel
        .querySelector('[data-products-slider]')
        ?.swiper?.slideTo(cards.findIndex((card) => card.dataset.productId === line.productId));

      const input = this.sizeList.querySelector(`[data-size-input][value="${line.variantId}"]`);
      if (input) {
        input.checked = true;
        this.selectSize(line.variantId);
        input.focus({ preventScroll: true });
      }

      this.setQuantity(line.quantity);
      this.updateAddButton();

      // Bring the product being edited to the middle of the screen. scrollBy, not
      // scrollIntoView: the sticky info column moves 1:1 with the page (see
      // family-bundle-product), which scrollIntoView does not account for.
      const panel = this.activePanel?.getBoundingClientRect();
      if (!panel) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollBy({
        top: panel.top + panel.height / 2 - window.innerHeight / 2,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    /** @param {number} index */
    removeLine(index) {
      const line = this.lines[index];
      if (!line) return;

      this.lines.splice(index, 1);

      if (this.editingIndex === index) {
        this.editingIndex = null;
      } else if (this.editingIndex !== null && this.editingIndex > index) {
        this.editingIndex -= 1;
      }

      this.announce(this.format(this.strings.removed, { item: this.lineName(line) }));
      this.setQuantity(this.quantity);
      this.updateAddButton();
      this.renderBundle();
    }

    renderBundle() {
      const bundle = this.querySelector('[data-bundle]');
      const list = this.querySelector('[data-bundle-list]');
      const fragment = document.createDocumentFragment();

      for (const [index, line] of this.lines.entries()) {
        fragment.append(this.createLineElement(line, index));
      }

      list.replaceChildren(fragment);
      bundle.hidden = this.lines.length === 0;
      this.renderSoldOutError();
    }

    /**
     * @param {BundleLine} line
     * @param {number} index
     * @returns {HTMLLIElement}
     */
    createLineElement(line, index) {
      const product = this.products[line.productId];
      const variant = product.variants.find((item) => item.id === line.variantId);
      const name = this.lineName(line);

      const item = document.createElement('li');
      item.className = 'family-bundle-builder__line';
      if (this.soldOutVariantIds.has(line.variantId)) item.classList.add('family-bundle-builder__line--sold-out');

      // Tile: image with Remove (✕) and Edit on it, then size and quantity. The
      // product name is not shown, so it goes in the image alt and button names.
      const media = document.createElement('div');
      media.className = 'family-bundle-builder__line-media';

      if (product.image) {
        const image = document.createElement('img');
        image.className = 'family-bundle-builder__line-image';
        image.src = product.image;
        image.alt = product.title;
        image.width = 120;
        image.height = 138;
        image.loading = 'lazy';
        media.append(image);
      }

      media.append(
        this.createLineButton('remove', index, '', this.format(this.strings.removeLabel, { item: name })),
        this.createLineButton('edit', index, this.strings.edit, this.format(this.strings.editLabel, { item: name }))
      );

      // "Size: M" on the left, the line total (price × quantity) on the right
      const size = document.createElement('p');
      size.className = 'family-bundle-builder__line-meta family-bundle-builder__line-meta--split';

      const sizeText = document.createElement('span');
      sizeText.textContent = this.format(this.strings.lineSize, { size: variant?.size || '' });
      size.append(sizeText);

      if (variant && typeof formatMoney === 'function') {
        const price = document.createElement('span');
        // No ".00" (or ",00") when the cents are zero, as on the product cards. Only a final
        // decimal part is trimmed, so "$74.50" and "Rs. 1,00,000" stay as they are.
        price.textContent = formatMoney(variant.price * line.quantity, window.moneyFormat).replace(/[.,]00(?=\D*$)/, '');
        size.append(price);
      }

      const quantity = document.createElement('p');
      quantity.className = 'family-bundle-builder__line-meta';
      quantity.textContent = this.format(this.strings.lineQuantity, { quantity: line.quantity });

      item.append(media, size, quantity);

      if (this.soldOutVariantIds.has(line.variantId)) {
        const soldOut = document.createElement('p');
        soldOut.className = 'family-bundle-builder__line-meta family-bundle-builder__line-meta--sold-out';
        soldOut.textContent = this.strings.lineSoldOut;
        item.append(soldOut);
      }

      return item;
    }

    /**
     * @param {'edit'|'remove'} action
     * @param {number} index
     * @param {string} text
     * @param {string} label
     * @returns {HTMLButtonElement}
     */
    createLineButton(action, index, text, label) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `family-bundle-builder__line-button family-bundle-builder__line-button--${action}`;
      button.dataset.lineAction = action;
      button.dataset.index = String(index);
      button.textContent = text;
      button.setAttribute('aria-label', label);
      return button;
    }

    /**
     * Checks live stock, then sends every line in one request. If any line has
     * sold out, nothing is added and every line stays so the shopper can
     * remove it. A partial add is never retried: it could double-add lines.
     * Every line carries hidden (underscore) properties so family page sales
     * can be reported from order data: the page, the print, and one group id
     * shared by the lines of this click.
     */
    async addToCart() {
      if (this.isAddingToCart || this.lines.length === 0) return;

      this.setCartBusy(true);
      this.showError('');

      try {
        const soldOut = await this.findSoldOutVariants();
        this.soldOutVariantIds = soldOut;

        if (soldOut.size > 0) {
          this.renderBundle();
          return;
        }

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const properties = {
          _family_page: 'true',
          _family_print: this.dataset.printHandle,
          _family_group: crypto.randomUUID(),
        };
        const body = {
          items: this.lines.map((line) => ({ id: line.variantId, quantity: line.quantity, properties })),
        };

        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        const response = await fetch(`${routes.cart_add_url}.js`, config);
        const data = await response.json();

        if (!response.ok || data.status) {
          this.showError(data.description || data.message || this.strings.cartError);
          return;
        }

        this.lines = [];
        this.editingIndex = null;
        this.soldOutVariantIds.clear();
        this.renderBundle();
        this.clearProduct();
        this.announce(this.strings.cartAdded);

        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'family-bundle', cartData: data });
        }

        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(data);
        } else {
          window.location.href = routes.cart_url;
        }
      } catch (error) {
        console.error(error);
        this.showError(this.strings.cartError);
      } finally {
        this.setCartBusy(false);
      }
    }

    /**
     * Fetches each bundle product fresh and returns the variant ids that are
     * no longer available.
     * @returns {Promise<Set<number>>}
     */
    async findSoldOutVariants() {
      const handles = [...new Set(this.lines.map((line) => this.products[line.productId].handle))];
      const root = window.Shopify?.routes?.root || '/';

      const results = await Promise.all(
        handles.map(async (handle) => {
          const response = await fetch(`${root}products/${handle}.js`, { cache: 'no-store' });
          if (!response.ok) throw new Error(`Product ${handle} returned ${response.status}`);
          return response.json();
        })
      );

      const available = new Map();

      for (const product of results) {
        for (const variant of product.variants) {
          available.set(variant.id, variant.available);
        }
      }

      const soldOut = new Set();

      for (const line of this.lines) {
        if (available.get(line.variantId) !== true) soldOut.add(line.variantId);
      }

      return soldOut;
    }

    /** Names every sold-out line still in the bundle; clears when none remain. */
    renderSoldOutError() {
      const soldOutLines = this.lines.filter((line) => this.soldOutVariantIds.has(line.variantId));

      if (soldOutLines.length === 0) {
        this.showError('');
        return;
      }

      // The same product and size can be on several lines; name it once.
      const names = [...new Set(soldOutLines.map((line) => this.lineName(line)))];
      const template = soldOutLines.length === 1 ? this.strings.soldOutOne : this.strings.soldOutMany;
      this.showError(this.format(template, { items: this.joinNames(names) }));
    }

    /** @param {string} message */
    showError(message) {
      this.querySelector('[data-error]').textContent = message;
    }

    /** @param {string} message */
    announce(message) {
      this.querySelector('[data-status]').textContent = message;
    }

    /** @param {boolean} isBusy */
    setCartBusy(isBusy) {
      this.isAddingToCart = isBusy;
      this.cartButton.disabled = isBusy;
      this.cartButton.setAttribute('aria-busy', String(isBusy));
    }

    /**
     * @param {BundleLine} line
     * @returns {string}
     */
    lineName(line) {
      const product = this.products[line.productId];
      const variant = product?.variants.find((item) => item.id === line.variantId);
      return this.format(this.strings.lineName, { title: product?.title || '', size: variant?.size || '' });
    }

    /**
     * "A", "A and B", "A, B and C".
     * @param {string[]} names
     * @returns {string}
     */
    joinNames(names) {
      if (names.length < 2) return names.join('');
      return `${names.slice(0, -1).join(', ')} ${this.strings.listAnd} ${names.at(-1)}`;
    }

    /**
     * Fills `%key%` tokens left in the translated strings by the snippet.
     * @param {string} template
     * @param {Record<string, string|number>} values
     * @returns {string}
     */
    format(template, values) {
      return Object.entries(values).reduce(
        (text, [key, value]) => text.replaceAll(`%${key}%`, String(value)),
        template
      );
    }
  }

  customElements.define('family-bundle-builder', FamilyBundleBuilder);
}
